'use client';

import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import Link from '@/components/ui/Link';

const ChevronIcon = ({ className = '', direction }: { className?: string; direction: 'left' | 'right' }) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d={direction === 'right' ? 'M9 18l6-6-6-6' : 'M15 18l-6-6 6-6'} />
  </svg>
);

interface Props {
  sectionSlug: string;
  currentSlug: string;
}

interface Neighbour {
  slug: string;
  headline: string;
}

/** Shared styling for both edge arrows; only the side differs. */
const ARROW_CLASS =
  'fixed top-1/2 -translate-y-1/2 z-[9999] flex items-center justify-center w-6 h-12 lg:w-10 lg:h-20 border border-gray-300 bg-white text-012061 hover:bg-012061 hover:text-white cursor-pointer shadow-lg transition-all';

/**
 * Fixed edge previous/next article arrows for the dynamic (article-template)
 * article page — the AMP article page already has these (renderAmpNavArrows)
 * and so does the legacy ArticleListInfinite single-mode renderer
 * (NavigationArrows, paging through its own client-fetched combinedArticles) —
 * this is the one gap: the article-template renderer had no equivalent at all.
 *
 * Both neighbours come from the article's POSITION in its section collection
 * (`/api/article-neighbours`), fetched client-side after the main article has
 * painted so it never blocks the initial load. Collections are ordered
 * newest-first, so the right arrow goes one step further down the section
 * (older) and the left arrow one step back up (newer) — the direction AMP's
 * `amp-next-page` walks.
 *
 * Positional lookup is why "previous" can exist here at all. AMP shows it only
 * when the reader arrives through its own next-arrow, which appends a `prev`
 * query param; a cold load from search or social has no such param and so gets
 * no back arrow. Reading the index out of the collection needs no navigation
 * history, so both arrows render on any load. It also makes "next" mean the
 * genuine next article — the previous implementation linked to the newest
 * article in the section that wasn't the current one, so from article #4 "next"
 * jumped back to #1.
 *
 * Either arrow is omitted when there's nothing in that direction (the newest
 * article in a section has no "previous", the oldest no "next").
 */
export default function ArticleNavArrows({ sectionSlug, currentSlug }: Props) {
  const [neighbours, setNeighbours] = useState<{ prev: Neighbour | null; next: Neighbour | null }>({
    prev: null,
    next: null,
  });
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  useEffect(() => {
    if (!sectionSlug) return;
    let cancelled = false;

    const url = `/api/article-neighbours?sectionSlug=${encodeURIComponent(sectionSlug)}&slug=${encodeURIComponent(currentSlug)}`;
    fetch(url)
      .then(res => (res.ok ? res.json() : null))
      .then(data => {
        if (cancelled || !data) return;
        setNeighbours({ prev: data.prev ?? null, next: data.next ?? null });
      })
      .catch(() => { /* no neighbour data — arrows simply don't render */ });

    return () => { cancelled = true; };
  }, [sectionSlug, currentSlug]);

  if (!isMounted) return null;

  const { prev, next } = neighbours;
  if (!prev && !next) return null;

  return createPortal(
    <>
      {prev && (
        <Link
          href={`/${prev.slug}`}
          aria-label={prev.headline ? `முந்தைய செய்தி: ${prev.headline}` : 'முந்தைய செய்தி'}
          title={prev.headline || undefined}
          className={`${ARROW_CLASS} left-0 rounded-r-md`}
        >
          <ChevronIcon direction="left" className="w-5 h-5 lg:w-7 lg:h-7" />
        </Link>
      )}
      {next && (
        <Link
          href={`/${next.slug}`}
          aria-label={next.headline ? `அடுத்த செய்தி: ${next.headline}` : 'அடுத்த செய்தி'}
          title={next.headline || undefined}
          className={`${ARROW_CLASS} right-0 rounded-l-md`}
        >
          <ChevronIcon direction="right" className="w-5 h-5 lg:w-7 lg:h-7" />
        </Link>
      )}
    </>,
    document.body
  );
}
